<template>
  <div class="border border-slate-200 bg-white p-4 shadow-sm">
    <div class="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2">
      <h3 class="shrink-0 text-base font-semibold leading-tight text-slate-800 sm:py-1">
        Dados do Período Filtrado
      </h3>
      <div class="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:justify-end">
        <TimeRangeFilter default-preset="24h" @change="onRangeChange" />
        <button
          type="button"
          class="flex items-center gap-1.5 border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-400"
          title="Maximizar gráfico"
          @click="openFullscreen">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
          Maximizar
        </button>
      </div>
    </div>

    <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
      <div class="border border-slate-200 border-l-4 border-l-slate-400 bg-white p-5 shadow-sm">
        <template v-if="isLoading">
          <span class="block h-10 w-28 animate-pulse bg-slate-200" />
          <span class="mt-3 block h-4 w-48 animate-pulse bg-slate-100" />
        </template>
        <template v-else>
          <p class="text-4xl font-bold tabular-nums text-slate-900">{{ formatStatTemperature(periodStats?.average) }}</p>
          <p class="mt-2 text-sm text-slate-600">Média do período selecionado</p>
        </template>
      </div>
      <div
        class="border border-slate-200 border-l-4 bg-white p-5 shadow-sm"
        :class="isPeakAtOrAboveCritical ? 'border-l-red-500' : 'border-l-slate-400'">
        <template v-if="isLoading">
          <span class="block h-10 w-28 animate-pulse bg-slate-200" />
          <span class="mt-3 block h-4 w-44 animate-pulse bg-slate-100" />
        </template>
        <template v-else>
          <p
            class="text-4xl font-bold tabular-nums"
            :class="isPeakAtOrAboveCritical ? 'text-red-700' : 'text-slate-900'">
            {{ formatStatTemperature(periodStats?.max) }}
          </p>
          <p class="mt-2 text-sm text-slate-600">Pico de temperatura no período</p>
        </template>
      </div>
    </div>

    <h3 class="mb-4 shrink-0 text-base font-semibold leading-tight text-slate-800 sm:py-1">
      Comportamento do Sensor
    </h3>

    <SensorChartAlarmCards
      v-model:expanded="alarmsSectionExpanded"
      :alarms="alarmList"
      :is-loading="isAlarmsLoading"
      accent="red"
      @select-alarm="openAlarmDetail"
      @hover-alarm="onAlarmHover" />

    <div ref="chartContainer" class="h-64 w-full min-h-[16rem]" />
    <p v-if="isLoading && chartData.length === 0" class="mt-2 text-center text-sm text-slate-500">
      Carregando...
    </p>
    <p v-else-if="!isLoading && chartData.length === 0" class="mt-2 text-center text-sm text-slate-500">
      Nenhum dado no período selecionado.
    </p>
  </div>

  <!-- Overlay tela cheia -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isFullscreen"
        class="fixed inset-0 z-[100000] flex flex-col bg-white"
        role="dialog"
        aria-modal="true"
        aria-label="Gráfico de temperatura em tela cheia">
        <div class="flex flex-none flex-col gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2">
            <h3 class="shrink-0 text-lg font-semibold leading-tight text-slate-800 sm:py-1">
              Dados do Período Filtrado
            </h3>
            <div class="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:justify-end">
              <TimeRangeFilter :initial-range="currentRange" default-preset="24h" @change="onRangeChange" />
              <button
                type="button"
                class="flex items-center gap-1.5 border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                title="Sair da tela cheia"
                @click="closeFullscreen">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Fechar
              </button>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="border border-slate-200 border-l-4 border-l-slate-400 bg-white p-5 shadow-sm">
              <template v-if="isLoading">
                <span class="block h-10 w-28 animate-pulse bg-slate-200" />
                <span class="mt-3 block h-4 w-48 animate-pulse bg-slate-100" />
              </template>
              <template v-else>
                <p class="text-4xl font-bold tabular-nums text-slate-900">{{ formatStatTemperature(periodStats?.average) }}</p>
                <p class="mt-2 text-sm text-slate-600">Média do período selecionado</p>
              </template>
            </div>
            <div
              class="border border-slate-200 border-l-4 bg-white p-5 shadow-sm"
              :class="isPeakAtOrAboveCritical ? 'border-l-red-500' : 'border-l-slate-400'">
              <template v-if="isLoading">
                <span class="block h-10 w-28 animate-pulse bg-slate-200" />
                <span class="mt-3 block h-4 w-44 animate-pulse bg-slate-100" />
              </template>
              <template v-else>
                <p
                  class="text-4xl font-bold tabular-nums"
                  :class="isPeakAtOrAboveCritical ? 'text-red-700' : 'text-slate-900'">
                  {{ formatStatTemperature(periodStats?.max) }}
                </p>
                <p class="mt-2 text-sm text-slate-600">Pico de temperatura no período</p>
              </template>
            </div>
          </div>
        </div>
        <div class="flex flex-1 flex-col overflow-auto p-4">
          <h3 class="mb-4 shrink-0 text-base font-semibold leading-tight text-slate-800 sm:py-1">
            Comportamento do Sensor
          </h3>
          <SensorChartAlarmCards
            v-model:expanded="alarmsSectionExpanded"
            :alarms="alarmList"
            :is-loading="isAlarmsLoading"
            accent="red"
            @select-alarm="openAlarmDetail"
            @hover-alarm="onAlarmHover" />
          <div ref="chartContainerFullscreen" class="min-h-[28rem] flex-1 w-full" />
          <p v-if="isLoading && chartData.length === 0" class="mt-2 text-center text-sm text-slate-500">Carregando...</p>
          <p v-else-if="!isLoading && chartData.length === 0" class="mt-2 text-center text-sm text-slate-500">Nenhum dado no período selecionado.</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { TimeRangeFilter } from '@/components/Globals/TimeRangeFilter'
import type { TimeRangeValue } from '@/components/Globals/TimeRangeFilter'
import SensorResource from '@/api/resources/sensor'
import SensorChartAlarmCards from '@/components/Sensors/SensorChartAlarmCards.vue'
import type { ITemperatureReadingsPeriodStats, ITemperatureSensorReadingsResponse } from '@/interfaces/api/sensor'
import type { ISensorChartOverlayAlarm } from '@/interfaces/components/sensorChartAlarm'
import {
  alarmsToPeriodsMs,
  fetchSensorChartOverlayAlarms,
  filterChartOverlayAlarms,
  resolveChartHoverIndex,
} from '@/utils/sensor-chart-alarms'
import { buildHoveredAlarmMarkArea } from '@/utils/components/alarm-detail'
import { buildTemperatureChartOption } from '@/utils/components/alarm-detail/temperature-chart-options'
import * as echarts from 'echarts'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const props = withDefaults(
  defineProps<{
    sensorId: number
    /** Temperatura crítica (setpoint); exibe linha guia horizontal no gráfico */
    setpoint?: number | null
  }>(),
  {
    setpoint: null,
  }
)

const chartContainer = ref<HTMLDivElement | null>(null)
const chartContainerFullscreen = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null
const isFullscreen = ref(false)
const alarmsSectionExpanded = ref(true)
const hoveredAlarmIndex = ref<number | null>(null)
const savedDataZoom = ref({ start: 0, end: 100 })
const currentRange = ref<TimeRangeValue | null>(null)

const isLoading = ref(false)
const isAlarmsLoading = ref(false)
const chartData = ref<[number, number][]>([])
const periodStats = ref<ITemperatureReadingsPeriodStats | null>(null)

const isPeakAtOrAboveCritical = computed(() => {
  const max = periodStats.value?.max
  const sp = props.setpoint
  if (max == null || Number.isNaN(Number(max))) return false
  if (sp == null || Number.isNaN(Number(sp))) return false
  return Number(max) >= Number(sp)
})

/** Períodos de alarme (temperatura crítica) no intervalo do filtro: [start_ms, end_ms][] */
const alarmPeriods = ref<[number, number][]>([])
const alarmList = ref<ISensorChartOverlayAlarm[]>([])

const sensorAlarms = computed(() => filterChartOverlayAlarms(alarmList.value))

function openAlarmDetail(alarm: ISensorChartOverlayAlarm) {
  if (alarm.origin === 'group') {
    if (!alarm.group?.id) return
    router.push({
      name: 'group-alarm-detail',
      params: { groupId: String(alarm.group.id), alarmId: String(alarm.id) },
      query: { alarmType: alarm.alarm_type ?? '' },
    })
    return
  }

  router.push({
    name: 'alarm-detail',
    params: {
      sensorId: String(props.sensorId),
      alarmId: String(alarm.id),
    },
    query: { alarmType: alarm.alarm_type ?? '' },
  })
}

function onAlarmHover(alarm: ISensorChartOverlayAlarm | null) {
  hoveredAlarmIndex.value = resolveChartHoverIndex(alarm, sensorAlarms.value)
}

/** window_period em minutos: intervalo maior para períodos longos */
function windowPeriodMinutes(range: TimeRangeValue): number {
  const hours = (range.end.getTime() - range.start.getTime()) / (60 * 60 * 1000)
  return hours > 48 ? 15 : 5
}

function parsePeriodStats(res: unknown): ITemperatureReadingsPeriodStats | null {
  if (!res || typeof res !== 'object') return null
  const r = res as ITemperatureSensorReadingsResponse
  let raw: ITemperatureReadingsPeriodStats | null | undefined = r.period_stats ?? undefined
  if (raw == null && r.readings && typeof r.readings === 'object' && !Array.isArray(r.readings)) {
    raw = r.readings.period_stats ?? undefined
  }
  if (raw == null || typeof raw !== 'object') return null
  const toNum = (v: unknown): number | null => {
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isNaN(n) ? null : n
  }
  const average = toNum(raw.average)
  const min = toNum(raw.min)
  const max = toNum(raw.max)
  if (average == null || min == null || max == null) return null
  return { average, min, max }
}

function formatStatTemperature(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return `${Number(value).toFixed(1)} °C`
}

async function fetchAlarmPeriods(range: TimeRangeValue) {
  if (!props.sensorId) return
  isAlarmsLoading.value = true
  try {
    const list = await fetchSensorChartOverlayAlarms({
      sensorId: props.sensorId,
      sensorAlarmType: 'critical_temperature',
      startDate: range.start.toISOString(),
      endDate: range.end.toISOString(),
    })
    alarmList.value = list
    const sensorOnly = filterChartOverlayAlarms(list)
    alarmPeriods.value = alarmsToPeriodsMs(sensorOnly, range.end.getTime())
  } catch {
    alarmList.value = []
    alarmPeriods.value = []
  } finally {
    isAlarmsLoading.value = false
  }
}

/** Busca leituras no intervalo selecionado (API Influx) */
async function fetchReadings(range: TimeRangeValue) {
  if (!props.sensorId) return
  isLoading.value = true
  try {
    const [readingsResRaw] = await Promise.all([
      SensorResource.getTemperatureSensorReadings({
        sensorId: props.sensorId,
        dateRange: `start_date=${encodeURIComponent(range.start.toISOString())}&end_date=${encodeURIComponent(range.end.toISOString())}`,
        resolution: `${windowPeriodMinutes(range)}m`,
      }),
      fetchAlarmPeriods(range),
    ])
    const readingsRes = readingsResRaw as ITemperatureSensorReadingsResponse
    periodStats.value = parsePeriodStats(readingsRes)
    const readings = readingsRes?.readings
    const raw = Array.isArray(readings)
      ? readings
      : (readings && typeof readings === 'object' && Array.isArray(readings.temperature)
          ? readings.temperature
          : readings && typeof readings === 'object' && Array.isArray(readings.Temperatura)
            ? readings.Temperatura
            : [])
    if (raw.length > 0) {
      chartData.value = raw
        .map((r: { t?: string; v?: number; time?: string; value?: number }) => {
          const timeStr = r.t ?? r.time
          const val = typeof (r.v ?? r.value) === 'number' && !Number.isNaN(Number(r.v ?? r.value)) ? Number(r.v ?? r.value) : null
          if (!timeStr || val === null) return [0, 0] as [number, number]
          return [new Date(timeStr).getTime(), val] as [number, number]
        })
        .filter(([t]) => t > 0)
    } else {
      chartData.value = []
    }
    savedDataZoom.value = { start: 0, end: 100 }
  } catch {
    chartData.value = []
    periodStats.value = null
    savedDataZoom.value = { start: 0, end: 100 }
  } finally {
    isLoading.value = false
  }
}

function onRangeChange(range: TimeRangeValue) {
  currentRange.value = range
  fetchReadings(range)
}

/** Extrai start/end do dataZoom da instância atual para preservar ao trocar de container */
function captureDataZoomState() {
  if (!chartInstance) return
  const opt = chartInstance.getOption() as { dataZoom?: Array<{ start?: number; end?: number }> } | undefined
  const dz = opt?.dataZoom
  if (Array.isArray(dz) && dz.length > 0) {
    const withRange = dz.find((d) => typeof (d as { start?: number; end?: number }).start === 'number' && typeof (d as { start?: number; end?: number }).end === 'number') as { start: number; end: number } | undefined
    if (withRange) savedDataZoom.value = { start: withRange.start, end: withRange.end }
  }
}

function openFullscreen() {
  captureDataZoomState()
  const zoomToRestore = { ...savedDataZoom.value }
  isFullscreen.value = true
  nextTick(() => {
    nextTick(() => {
      savedDataZoom.value = { ...zoomToRestore }
      initChart()
      nextTick(() => chartInstance?.resize())
    })
  })
}

function closeFullscreen() {
  captureDataZoomState()
  isFullscreen.value = false
  nextTick(() => {
    initChart()
    nextTick(() => chartInstance?.resize())
  })
}

function getActiveChartContainer(): HTMLDivElement | null {
  return isFullscreen.value ? chartContainerFullscreen.value : chartContainer.value
}

function initChart() {
  const container = getActiveChartContainer()
  if (!container) return
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  chartInstance = echarts.init(container)
  updateChart()
}

function updateChart() {
  if (!chartInstance) return

  const setpoint = props.setpoint != null && !Number.isNaN(Number(props.setpoint)) ? Number(props.setpoint) : null

  const option = buildTemperatureChartOption({
    chartData: chartData.value,
    dataZoom: savedDataZoom.value,
    markArea: buildHoveredAlarmMarkArea(alarmPeriods.value, hoveredAlarmIndex.value),
    setpointMarkLine:
      setpoint != null
        ? {
            symbol: 'none',
            lineStyle: { type: 'dashed', color: '#dc2626', width: 2 },
            label: {
              show: true,
              position: 'end',
              formatter: `Crítico: ${setpoint.toFixed(0)} °C`,
              color: '#dc2626',
              fontSize: 10,
              fontWeight: 'bold',
            },
            data: [{ yAxis: setpoint }],
          }
        : undefined,
    showLegend: true,
    showSeriesName: false,
    connectNulls: false,
    sliderBottom: 28,
    showMoveHandle: true,
  })

  chartInstance.setOption(option)
}

watch(
  [chartData, alarmPeriods, hoveredAlarmIndex],
  () => {
    nextTick(() => updateChart())
  },
  { deep: true },
)

watch(() => props.setpoint, () => {
  nextTick(() => updateChart())
})

watch(isFullscreen, () => {
  nextTick(() => chartInstance?.resize())
})

const handleResize = () => chartInstance?.resize()

onMounted(() => {
  nextTick(() => initChart())
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
